import axios, { AxiosError, AxiosResponse } from 'axios';
import config from '../config';
import {
  getDriveApiToken
} from '../lib/auth-token';
import {
  maybeBeginDriveReauthOn401
} from '../lib/drive-auth-api';

// Error types
export enum ApiErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
  AUTH_ERROR = 'AUTH_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

// Error response interface
interface ErrorResponse {
  message?: string;
  error?: string;
  code?: string;
  details?: any;
  path?: string;
}

// Error interface
export interface ApiError {
  type: ApiErrorType;
  message: string;
  status?: number;
  data?: ErrorResponse;
  originalError?: any;
  requestInfo?: {
    url?: string;
    method?: string;
    params?: any;
    data?: any;
  };
}

export const isRetryableApiError = (apiError: ApiError): boolean => {
  if (!apiError.status) {
    return apiError.type === ApiErrorType.NETWORK_ERROR || apiError.type === ApiErrorType.TIMEOUT_ERROR;
  }

  return apiError.status === 408 || apiError.status === 425 || apiError.status === 429 || apiError.status >= 500;
};

// Error messages
const ERROR_MESSAGES = {
  [ApiErrorType.NETWORK_ERROR]: 'Network error. Please check your internet connection.',
  [ApiErrorType.TIMEOUT_ERROR]: 'Request timed out. Please try again.',
  [ApiErrorType.SERVER_ERROR]: 'Server error. Please try again later.',
  [ApiErrorType.AUTH_ERROR]: 'Authentication error. Redirecting to sign-in.',
  [ApiErrorType.VALIDATION_ERROR]: 'Validation error. Please check your input.',
  [ApiErrorType.UNKNOWN_ERROR]: 'An unexpected error occurred. Please try again.'
};

// Create error object from axios error
export const createApiError = (error: AxiosError<ErrorResponse>): ApiError => {
  const requestInfo = {
    url: error.config?.url,
    method: error.config?.method?.toUpperCase(),
    params: error.config?.params,
    data: error.config?.data
  };

  if (!error.response) {
    if (error.code === 'ECONNABORTED') {
      return {
        type: ApiErrorType.TIMEOUT_ERROR,
        message: ERROR_MESSAGES[ApiErrorType.TIMEOUT_ERROR],
        originalError: error,
        requestInfo
      };
    }
    return {
      type: ApiErrorType.NETWORK_ERROR,
      message: ERROR_MESSAGES[ApiErrorType.NETWORK_ERROR],
      originalError: error,
      requestInfo
    };
  }

  const { status, data } = error.response;

  switch (status) {
    case 401:
      return {
        type: ApiErrorType.AUTH_ERROR,
        message: data?.message || ERROR_MESSAGES[ApiErrorType.AUTH_ERROR],
        status,
        data,
        originalError: error,
        requestInfo
      };
    case 400:
      return {
        type: ApiErrorType.VALIDATION_ERROR,
        message: data?.message || ERROR_MESSAGES[ApiErrorType.VALIDATION_ERROR],
        status,
        data,
        originalError: error,
        requestInfo
      };
    case 500:
    case 502:
    case 503:
    case 504: {
      let serverErrorMessage = data?.message || ERROR_MESSAGES[ApiErrorType.SERVER_ERROR];
      if (data?.path) {
        serverErrorMessage = `Error with ${data.path}. ${serverErrorMessage}`;
      }

      return {
        type: ApiErrorType.SERVER_ERROR,
        message: serverErrorMessage,
        status,
        data,
        originalError: error,
        requestInfo
      };
    }
    default:
      return {
        type: ApiErrorType.UNKNOWN_ERROR,
        message: data?.message || ERROR_MESSAGES[ApiErrorType.UNKNOWN_ERROR],
        status,
        data,
        originalError: error,
        requestInfo
      };
  }
};

// Handle API error — never redirect on 403/404/5xx; 401 goes through beginDriveReauth
export const handleApiError = (error: AxiosError<ErrorResponse>): void => {
  if (error.code === "ERR_CANCELED" || error.name === "CanceledError" || error.name === "AbortError") return;
  const apiError = createApiError(error);

  if (config.ui.errors.showErrorNotifications) {
    if (window.enqueueSnackbar && apiError.type !== ApiErrorType.AUTH_ERROR) {
      window.enqueueSnackbar(apiError.message, { variant: 'error' });
    }
  }
};

function resolveRequestPath(error: AxiosError): string {
  const url = error.config?.url || '';
  const base = error.config?.baseURL || '';
  try {
    if (url.startsWith('http')) return new URL(url).pathname;
    if (base.startsWith('http')) return new URL(url, base).pathname;
  } catch {
    /* ignore */
  }
  return url || base || '';
}

// Create axios instance with error handling
export const createAxiosInstance = (baseURL: string) => {
  const instance = axios.create({
    baseURL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json'
    }
  });

  instance.interceptors.request.use(
    (reqConfig) => {
      const token = getDriveApiToken();
      if (token) {
        reqConfig.headers.Authorization = `Bearer ${token}`;
      }
      return reqConfig;
    },
    (error) => {
      handleApiError(error);
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError<ErrorResponse>) => {
      const status = error.response?.status;
      const path = resolveRequestPath(error);
      const hadBearer = Boolean(
        error.config?.headers?.Authorization || getDriveApiToken()
      );

      // 401 + session expired + Bearer was sent → One resume (no mutating retry)
      // beginDriveReauth clears the token; do not redirect on 403/404/5xx
      if (status === 401) {
        maybeBeginDriveReauthOn401({
          path,
          status,
          body: error.response?.data,
          hadBearer
        });
      }

      handleApiError(error);
      return Promise.reject(error);
    }
  );

  return instance;
};

// Retry failed requests
export const retryRequest = async (
  requestFn: () => Promise<any>,
  maxRetries: number = config.ui.errors.maxRetries
): Promise<any> => {
  let lastError: any;

  for (let i = 0; i < maxRetries; i++) {
    try {
      return await requestFn();
    } catch (error) {
      if ((error as any)?.code === "ERR_CANCELED" || (error as any)?.name === "AbortError") throw error;
      lastError = error;
      const apiError = createApiError(error as AxiosError<ErrorResponse>);

      if (
        !isRetryableApiError(apiError) ||
        apiError.type === ApiErrorType.AUTH_ERROR ||
        apiError.type === ApiErrorType.VALIDATION_ERROR
      ) {
        throw error;
      }

      const delay = Math.min(1000 * Math.pow(2, i), 10000);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
};

declare global {
  interface Window {
    enqueueSnackbar: (message: string, options?: any) => void;
  }
}

const apiErrorHandler = {
  createApiError,
  handleApiError,
  createAxiosInstance,
  retryRequest
};

export default apiErrorHandler;
